import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { isSubscriptionMember, isClassicLens, isActiveMembership } from "helpers/membership-helpers";
import RtrTabs from "components/design-system/rtr-tabs";
import HeapHelpers from "helpers/heap-helpers";
import { membershipLens } from "rtr-constants";
import SubscriptionPromoBanner from "../shared/subscription-promo-banner";
import { useSelector } from "react-redux";
import ReservePromoBanner from "../shared/reserve-promo-banner";

const PdpAddToBagTabs = ({
  userData = {},
  product = {},
  reservation = {},
  handleWayfindCtaClick = () => {},
  renderMembershipFormFields = () => {},
  renderRentableFormFields = () => {},
}) => {
  const cmsSubCopy = useSelector(state => state.smartComponentState?.PDPContent?.data?.membership);
  const cmsReserveCopy = useSelector(state => state.smartComponentState?.PDPContent?.data?.reserve);
  const activeMember = useSelector(state => isActiveMembership(state.membershipState));
  const [tabSelected, setTabSelected] = React.useState(isClassicLens(userData) ? "Reserve" : "Subscription");

  const subscriptionRef = useRef(null);
  const reserveRef = useRef(null);
  const promoContainerRef = useRef(null);

  const reserveTabTitle = cmsReserveCopy?.reserveTabTitle || "One-off rental";
  const reserveTabSubText = isSubscriptionMember(userData)
    ? cmsReserveCopy?.reserveTabSubTextIfSub || "Reserve in advance"
    : cmsReserveCopy?.reserveTabSubText || "No subscription required";

  const reserveTabPricingCopySubText =
    cmsReserveCopy?.pricingSubText || "Reserve specific dates with free backup size included";

  const subTabTitle = cmsSubCopy?.subscriptionTabTitle || "Subscription";
  const subTabSubText = isSubscriptionMember(userData) ? (
    cmsSubCopy?.subscriptionTabSubTextIfSub || "Included with membership"
  ) : (
    <span className={!isClassicLens(userData) ? "text-tealGreen" : ""}>
      {cmsSubCopy?.subscriptionTabSubText || "Best Value"}
    </span>
  );

  const subTabPricingCopy = cmsSubCopy?.pricingCopy || "Included with subscription";
  const subTabPricingCopySubText = cmsSubCopy?.pricingCopySubtext || "Rent 5 items at a time with our flexible";
  const subTabPricingCopySubtextLinkCopy = cmsSubCopy?.pricingCopySubtextLinkCopy || "monthly plans";

  // Bleh so: we need to heap track user scroll on the container but we do not want to include scrolling that happens when the tab is selected.
  // Hence, checking for an actual touch/mousewheel event before firing the heap scroll event.
  useEffect(() => {
    if (!promoContainerRef?.current) return;

    let fired = false;
    const lastScrollLeft = promoContainerRef.current.scrollLeft;
    let isUserInteracting = false;

    const handleScroll = () => {
      if (fired || !isUserInteracting) return;

      if (promoContainerRef.current.scrollLeft !== lastScrollLeft) {
        fired = true;
        HeapHelpers.fireHeapEvent("userScrolledPdpPromoContainer", {});
        cleanup();
      }
    };

    const markUserInteraction = () => {
      isUserInteracting = true;
    };

    const cleanup = () => {
      if (!promoContainerRef?.current) return;

      promoContainerRef.current.removeEventListener("scroll", handleScroll);
      promoContainerRef.current.removeEventListener("wheel", markUserInteraction);
      promoContainerRef.current.removeEventListener("touchstart", markUserInteraction);
    };

    promoContainerRef.current.addEventListener("scroll", handleScroll);
    promoContainerRef.current.addEventListener("wheel", markUserInteraction, { passive: true });
    promoContainerRef.current.addEventListener("touchstart", markUserInteraction, { passive: true });

    return cleanup;
  }, []);

  useEffect(() => {
    if (tabSelected === "Subscription") {
      subscriptionRef.current?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    } else if (tabSelected === "Reserve") {
      reserveRef.current?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }
  }, [tabSelected]);

  const tabReserveButton = (
    <div className="flex flex-col">
      <span>{reserveTabTitle}</span>
      <span className="text-sm mt-0.5">{reserveTabSubText}</span>
    </div>
  );

  const tabMembershipButton = (
    <div className="flex flex-col">
      <span>{subTabTitle}</span>
      <span className="text-sm mt-1">{subTabSubText}</span>
    </div>
  );

  const tabReserveContent = (
    <div className="mb-4">
      <div className="flex flex-col gap-1 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {product?.price?.adjusted !== product?.price?.base && (
              <span className="text-darkGray line-through">${product?.price?.base}</span>
            )}
            {!reservation?.duration && "Starting from"}
            {`$${product?.price?.adjusted}`}
            {reservation?.duration && (
              <div className="flex gap-1 items-center">
                |<span>{reservation?.duration}-day rental</span>
              </div>
            )}
          </div>
          {isSubscriptionMember(userData) ? <span className="text-tealGreen text-sm">Members get 25% off</span> : null}
        </div>
        {isSubscriptionMember(userData) ? null : <p className="text-sm">{reserveTabPricingCopySubText}</p>}
      </div>
      {renderRentableFormFields()}
    </div>
  );

  const heapTabClickEvent = tabId => {
    HeapHelpers.fireHeapEvent("tabbed_lens_switch_on_pdp", { lensTabClicked: tabId });
  };

  const tabMembershipContent = (
    <div className="mb-4">
      {!activeMember ? (
        <div className="flex flex-col gap-1 mb-4">
          <p>{subTabPricingCopy}</p>
          <p className="text-sm">
            {subTabPricingCopySubText}{" "}
            <a href={cmsSubCopy?.pricingCopySubtextLinkUrl || "/plans"} className="font-semibold underline">
              {subTabPricingCopySubtextLinkCopy}
            </a>
          </p>
        </div>
      ) : null}
      {renderMembershipFormFields()}
    </div>
  );

  const tabData = [
    {
      id: "Subscription",
      additionalCallback: () => {
        if (isClassicLens(userData)) {
          setTabSelected("Subscription");
          heapTabClickEvent("Subscription");
          handleWayfindCtaClick();
        }
      },
      label: tabMembershipButton,
      content: tabMembershipContent,
    },
    {
      id: "Reserve",
      additionalCallback: () => {
        if (!isClassicLens(userData)) {
          setTabSelected("Reserve");
          heapTabClickEvent("Reserve");
          handleWayfindCtaClick(membershipLens.classic);
        }
      },
      label: tabReserveButton,
      content: tabReserveContent,
    },
  ];

  return (
    <div>
      {!isSubscriptionMember(userData) && (
        <div
          ref={promoContainerRef}
          data-test-id="promo-scroll-container"
          className="flex gap-2 flex-nowrap overflow-x-scroll overflow-visible scrollbar-hidden pb-4 -mr-4">
          <div className="w-3/4 shrink-0" ref={subscriptionRef}>
            <SubscriptionPromoBanner />
          </div>
          <div className="w-3/4 shrink-0 mr-4" ref={reserveRef}>
            <ReservePromoBanner />
          </div>
        </div>
      )}
      <RtrTabs
        className="-mx-4 text-onyx"
        tabs={tabData}
        activeTabId={isClassicLens(userData) ? "Reserve" : "Subscription"}
        dataTestId="pdp-add-to-bag-tabs"
      />
    </div>
  );
};

PdpAddToBagTabs.propTypes = {
  userData: PropTypes.object,
  product: PropTypes.object,
  handleWayfindCtaClick: PropTypes.func,
  reservation: PropTypes.object,
  renderMembershipFormFields: PropTypes.func,
  renderRentableFormFields: PropTypes.func,
};

export default PdpAddToBagTabs;
